import React, { useState, useEffect } from "react";
import { supabase } from "../supabase";
import { useAuth } from "../contexts/AuthContext";
import { useProfileInfo } from "../contexts/ProfileInfoContext";
import useSnackbarNotification from "../hooks/useSnackbarNotification";
import Select from "react-select";
import countryList from "react-select-country-list";
import { State } from "country-state-city";

const JobApplicationModal = ({
    isOpen,
    onClose,
    jobId,
    jobTitle,
    onNext,
    step1Data,
    onBack,
    screeningQuestionsEnabled = false,
    screeningQuestionsList = [],
    driversLicenseRequired = false,
    visaSponsorshipOffered = false
}) => {
    const { user } = useAuth();
    const { profileInfo } = useProfileInfo();
    const { showError, showSuccess } = useSnackbarNotification();

    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        email: "",
        cellNumber: "",
        cvFile: null,
        coverLetterFile: null,
    });

    // Stores answers to dynamic employer screening questions
    // keyed by question index: { 0: "Yes", 1: "No", ... }
    const [screeningAnswers, setScreeningAnswers] = useState({});
    
    // Stores answers to drivers license and visa sponsorship questions
    const [driversLicenseAnswer, setDriversLicenseAnswer] = useState(null);
    const [visaSponsorshipAnswer, setVisaSponsorshipAnswer] = useState(null);

    const [uploading, setUploading] = useState({
        cv: false,
        coverLetter: false,
    });

    const [uploadedFiles, setUploadedFiles] = useState({
        cv: null,
        coverLetter: null,
    });

    const [dragActive, setDragActive] = useState({
        cv: false,
        coverLetter: false,
    });

    const [imageError, setImageError] = useState(false);

    // Populate form with user data
    useEffect(() => {
        if (profileInfo && user) {
            setFormData((prev) => ({
                ...prev,
                email: user.email || profileInfo.email || "",
                cellNumber: user?.user_metadata?.cell_number || profileInfo.cellnumber || "",
            }));
            setImageError(false); // Reset image error when profile changes
        }
    }, [profileInfo, user]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(1);
            setFormData({
                email: "",
                cellNumber: "",
                cvFile: null,
                coverLetterFile: null,
            });
            setScreeningAnswers({});
            setDriversLicenseAnswer(null);
            setVisaSponsorshipAnswer(null);
            setUploadedFiles({
                cv: null,
                coverLetter: null,
            });
            setImageError(false);
        }
    }, [isOpen]);

    const hasCustomScreeningQuestions =
        screeningQuestionsEnabled &&
        Array.isArray(screeningQuestionsList) &&
        screeningQuestionsList.length > 0;
    
    // Check if there are any screening questions (custom, drivers license, or visa sponsorship)
    const hasScreeningQuestions = hasCustomScreeningQuestions || driversLicenseRequired || visaSponsorshipOffered;

    const totalSteps = hasScreeningQuestions ? 2 : 1;

    // If step1Data is provided, we're on step 2 (or completion if no screening questions)
    // If step1Data is null/undefined, we're on step 1
    useEffect(() => {
        if (step1Data) {
            // If there are screening questions, go to Step 2, otherwise go straight to completion screen
            setCurrentStep(hasScreeningQuestions ? 2 : 3);
        } else {
            setCurrentStep(1);
        }
    }, [step1Data, hasScreeningQuestions]);

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const validateFile = (file) => {
        const validTypes = [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ];
        const validExtensions = [".pdf", ".doc", ".docx"];
        const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf("."));

        if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
            return { valid: false, message: "Please upload a PDF, DOC, or DOCX file" };
        }

        if (file.size > 10 * 1024 * 1024) {
            return { valid: false, message: "File size must be less than 10 MB" };
        }

        return { valid: true };
    };

    const handleFileUpload = async (file, type) => {
        if (!file || !user) return;

        const validation = validateFile(file);
        if (!validation.valid) {
            showError(validation.message);
            return;
        }

        try {
            setUploading((prev) => ({ ...prev, [type]: true }));

            const bucket = process.env.REACT_APP_CV_BUCKET || "my-cv-uploads";
            const filePath = `${user.id}/applications/${jobId}/${Date.now()}_${file.name}`;

            const { error: uploadError } = await supabase.storage
                .from(bucket)
                .upload(filePath, file, {
                    upsert: true,
                    contentType: file.type || "application/octet-stream",
                });

            if (uploadError) throw uploadError;

            const { data: publicData } = supabase.storage.from(bucket).getPublicUrl(filePath);
            const publicUrl = publicData?.publicUrl || "";

            setUploadedFiles((prev) => ({
                ...prev,
                [type]: { name: file.name, url: publicUrl, path: filePath },
            }));

            setFormData((prev) => ({
                ...prev,
                [type === "cv" ? "cvFile" : "coverLetterFile"]: file,
            }));

            showSuccess(`${type === "cv" ? "CV" : "Cover Letter"} uploaded successfully`);
        } catch (error) {
            console.error(`Error uploading ${type}:`, error);
            showError(`Failed to upload ${type === "cv" ? "CV" : "Cover Letter"}. Please try again.`);
        } finally {
            setUploading((prev) => ({ ...prev, [type]: false }));
        }
    };

    const handleFileInput = (e, type) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileUpload(file, type);
        }
    };

    const handleDrag = (e, type) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive((prev) => ({ ...prev, [type]: true }));
        } else if (e.type === "dragleave") {
            setDragActive((prev) => ({ ...prev, [type]: false }));
        }
    };

    const handleDrop = (e, type) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive((prev) => ({ ...prev, [type]: false }));

        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleFileUpload(file, type);
        }
    };

    const handleRemoveFile = (type) => {
        setUploadedFiles((prev) => ({ ...prev, [type]: null }));
        setFormData((prev) => ({
            ...prev,
            [type === "cv" ? "cvFile" : "coverLetterFile"]: null,
        }));
    };

    const handleStep1Next = () => {
        // Validate required fields
        if (!formData.email) {
            showError("Email address is required");
            return;
        }

        if (!uploadedFiles.cv) {
            showError("Please upload your CV");
            return;
        }

        if (!uploadedFiles.coverLetter) {
            showError("Please upload your cover letter");
            return;
        }

        // Call parent's onNext with form data to move to step 2
        if (onNext) {
            onNext({
                email: formData.email,
                cellNumber: formData.cellNumber,
                cvFile: uploadedFiles.cv,
                coverLetterFile: uploadedFiles.coverLetter,
            });
        }
    };

    const handleStep2Next = () => {
        if (hasScreeningQuestions) {
            // Ensure all custom screening questions have an answer
            if (hasCustomScreeningQuestions) {
                const unansweredIndex = screeningQuestionsList.findIndex(
                    (_q, idx) => !screeningAnswers[idx]
                );

                if (unansweredIndex !== -1) {
                    showError("Please answer all screening questions before continuing.");
                    return;
                }
            }
            
            // Validate drivers license answer if required
            if (driversLicenseRequired && !driversLicenseAnswer) {
                showError("Please answer the driver's license question.");
                return;
            }
            
            // Validate visa sponsorship answer if required
            if (visaSponsorshipOffered && !visaSponsorshipAnswer) {
                showError("Please answer the visa sponsorship question.");
                return;
            }
        }

        // Move to completion screen
        setCurrentStep(3);
    };

    const handleStep3Done = () => {
        // Submit final application with both step 1 and screening answers (if any)
        if (onNext) {
            const finalData = {
                ...step1Data,
                step2Answers: screeningAnswers,
                driversLicenseAnswer: driversLicenseAnswer,
                visaSponsorshipAnswer: visaSponsorshipAnswer,
            };
            
            // Debug logging
            console.log('=== JobApplicationModal handleStep3Done ===');
            console.log('step1Data:', step1Data);
            console.log('Final data being sent:', finalData);
            console.log('CV File in finalData:', finalData.cvFile);
            console.log('Cover Letter File in finalData:', finalData.coverLetterFile);
            
            onNext(finalData);
        }
    };
    
    const handleScreeningAnswerChange = (questionIndex, value) => {
        setScreeningAnswers((prev) => ({
            ...prev,
            [questionIndex]: value,
        }));
    };

    const handleBack = () => {
        // Clear step1Data in parent to go back to step 1
        if (onBack) {
            onBack();
        }
    };

    if (!isOpen) return null;

    // Get user profile data for display
    const firstName = profileInfo?.firstname || "";
    const lastName = profileInfo?.lastname || "";
    const fullName = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || "User";
    const profession = profileInfo?.profession || "Medical Professional";
    const profileImageUrl = profileInfo?.profile_image_url || "";
    
    // Get location from user metadata (country and state from onboarding)
    const userCountry = user?.user_metadata?.country || profileInfo?.country || "";
    const userCountryCode = user?.user_metadata?.country_code || profileInfo?.country_code || "";
    const userProvinceState = user?.user_metadata?.province_state || profileInfo?.province_state || "";
    
    // Format location display
    const location = userCountry && userProvinceState 
        ? `${userProvinceState}, ${userCountry}`
        : userCountry || profileInfo?.location_city_country || "Location not specified";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={onClose}
            ></div>

            {/* Modal */}
            <div className={`relative bg-white w-full ${currentStep === 2 || currentStep === 3 ? 'max-w-3xl' : 'max-w-2xl'} mx-4 rounded-2xl shadow-2xl border border-gray-200 max-h-[90vh] overflow-hidden flex flex-col`}>
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white">
                    <h2 className="text-xl font-semibold text-gray-900">Apply for job</h2>
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-500">
                            {currentStep === 3
                                ? "Complete"
                                : `Step ${currentStep} of ${totalSteps}`}
                        </span>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            aria-label="Close modal"
                        >
                            <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto px-6 py-6">
                    {currentStep === 1 && (
                        <>
                    {/* Applicant Profile Section */}
                    <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-200">
                        <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0 ring-2 ring-gray-100">
                            {profileImageUrl && !imageError ? (
                                <img
                                    src={profileImageUrl}
                                    alt={fullName}
                                    className="w-full h-full object-cover"
                                    onError={() => setImageError(true)}
                                />
                            ) : (
                                <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xl font-semibold">
                                    {fullName.charAt(0).toUpperCase()}
                                </div>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold text-gray-900 truncate">
                                {fullName}
                            </h3>
                            <p className="text-sm text-gray-600 truncate">{profession} | {location}</p>
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mb-6">
                        <div>
                            <label className="block text-sm text-gray-700 mb-1">
                                Email Address <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleInputChange("email", e.target.value)}
                                className="rounded-full border border-gray-300 px-4 py-2 w-full text-sm focus:ring-2 focus:ring-black focus:border-transparent outline-none transition-all"
                                placeholder="your.email@example.com"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm text-gray-700 mb-1">
                                Cell Number
                            </label>
                            <input
                                type="tel"
                                value={formData.cellNumber}
                                onChange={(e) => handleInputChange("cellNumber", e.target.value)}
                                className="rounded-full border border-gray-300 px-4 py-2 w-full text-sm focus:ring-2 focus:ring-black focus:border-transparent outline-none transition-all"
                                placeholder="+27 12 345 6789"
                            />
                        </div>
                    </div>

                    {/* Document Upload Sections */}
                    <div className="space-y-4">
                        {/* CV Upload */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Upload updated CV <span className="text-red-500">*</span>
                            </label>
                            {uploadedFiles.cv ? (
                                <div className="border border-gray-300 rounded-full px-4 py-3 bg-gray-50 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <svg
                                            className="w-5 h-5 text-gray-600"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                            />
                                        </svg>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">
                                                {uploadedFiles.cv.name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {uploading.cv ? "Uploading..." : "Uploaded successfully"}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleRemoveFile("cv")}
                                        className="text-gray-400 hover:text-red-500 transition-colors"
                                        disabled={uploading.cv}
                                    >
                                        <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            ) : (
                                <div
                                    onDragEnter={(e) => handleDrag(e, "cv")}
                                    onDragLeave={(e) => handleDrag(e, "cv")}
                                    onDragOver={(e) => handleDrag(e, "cv")}
                                    onDrop={(e) => handleDrop(e, "cv")}
                                    className={`border border-gray-300 rounded-full px-4 py-3 transition-colors ${
                                        dragActive.cv
                                            ? "border-blue-500 bg-blue-50"
                                            : "bg-white hover:border-gray-400"
                                    } ${uploading.cv ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                                >
                                    <input
                                        type="file"
                                        id="cv-upload"
                                        accept=".pdf,.doc,.docx"
                                        className="hidden"
                                        onChange={(e) => handleFileInput(e, "cv")}
                                        disabled={uploading.cv}
                                    />
                                    <label
                                        htmlFor="cv-upload"
                                        className="cursor-pointer flex items-center gap-2"
                                    >
                                        <svg
                                            className="w-5 h-5 text-gray-600"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                            />
                                        </svg>
                                        <span className="text-sm text-gray-700">
                                            Drop files here or <span className="underline">browse</span>
                                        </span>
                                    </label>
                                    {uploading.cv && (
                                        <p className="text-xs text-gray-500 mt-1 ml-7">Uploading...</p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Cover Letter Upload */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Upload your Cover Letter <span className="text-red-500">*</span>
                            </label>
                            {uploadedFiles.coverLetter ? (
                                <div className="border border-gray-300 rounded-full px-4 py-3 bg-gray-50 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <svg
                                            className="w-5 h-5 text-gray-600"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                            />
                                        </svg>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">
                                                {uploadedFiles.coverLetter.name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {uploading.coverLetter
                                                    ? "Uploading..."
                                                    : "Uploaded successfully"}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleRemoveFile("coverLetter")}
                                        className="text-gray-400 hover:text-red-500 transition-colors"
                                        disabled={uploading.coverLetter}
                                    >
                                        <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            ) : (
                                <div
                                    onDragEnter={(e) => handleDrag(e, "coverLetter")}
                                    onDragLeave={(e) => handleDrag(e, "coverLetter")}
                                    onDragOver={(e) => handleDrag(e, "coverLetter")}
                                    onDrop={(e) => handleDrop(e, "coverLetter")}
                                    className={`border border-gray-300 rounded-full px-4 py-3 transition-colors ${
                                        dragActive.coverLetter
                                            ? "border-blue-500 bg-blue-50"
                                            : "bg-white hover:border-gray-400"
                                    } ${uploading.coverLetter ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                                >
                                    <input
                                        type="file"
                                        id="cover-letter-upload"
                                        accept=".pdf,.doc,.docx"
                                        className="hidden"
                                        onChange={(e) => handleFileInput(e, "coverLetter")}
                                        disabled={uploading.coverLetter}
                                    />
                                    <label
                                        htmlFor="cover-letter-upload"
                                        className="cursor-pointer flex items-center gap-2"
                                    >
                                        <svg
                                            className="w-5 h-5 text-gray-600"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                            />
                                        </svg>
                                        <span className="text-sm text-gray-700">
                                            Drop files here or <span className="underline">browse</span>
                                        </span>
                                    </label>
                                    {uploading.coverLetter && (
                                        <p className="text-xs text-gray-500 mt-1 ml-7">Uploading...</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                        </>
                    )}
                    {currentStep === 2 && hasScreeningQuestions && (
                        /* Step 2: Dynamic Employer Screening Questions */
                        <div className="space-y-10">
                            {/* Custom screening questions */}
                            {hasCustomScreeningQuestions && screeningQuestionsList.map((questionObj, qIndex) => {
                                const questionType = questionObj.type || (questionObj.options ? "multiple-choice" : "text");
                                return (
                                    <div key={qIndex}>
                                        <label className="block text-sm font-medium text-gray-900 mb-3">
                                            {questionObj.question} <span className="text-red-500">*</span>
                                        </label>
                                        {questionType === "text" ? (
                                            <textarea
                                                value={screeningAnswers[qIndex] || ""}
                                                onChange={(e) =>
                                                    handleScreeningAnswerChange(
                                                        qIndex,
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Type your answer here..."
                                                rows={4}
                                                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent resize-y"
                                            />
                                        ) : (
                                            <div className="flex flex-wrap items-center gap-4">
                                                {(questionObj.options || []).map((option, oIndex) => (
                                                    <label
                                                        key={oIndex}
                                                        className="flex items-center gap-2 cursor-pointer"
                                                    >
                                                        <input
                                                            type="radio"
                                                            name={`screening-question-${qIndex}`}
                                                            value={option}
                                                            checked={screeningAnswers[qIndex] === option}
                                                            onChange={(e) =>
                                                                handleScreeningAnswerChange(
                                                                    qIndex,
                                                                    e.target.value
                                                                )
                                                            }
                                                            className="w-4 h-4 accent-black border-gray-300 focus:ring-black"
                                                        />
                                                        <span className="text-sm text-gray-700">
                                                            {option}
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            
                            {/* Driver's License Question */}
                            {driversLicenseRequired && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-900 mb-3">
                                        Do you have a driver's license? <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex flex-wrap items-center gap-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="drivers-license-question"
                                                value="yes"
                                                checked={driversLicenseAnswer === "yes"}
                                                onChange={() => setDriversLicenseAnswer("yes")}
                                                className="w-4 h-4 accent-black border-gray-300 focus:ring-black"
                                            />
                                            <span className="text-sm text-gray-700">Yes</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="drivers-license-question"
                                                value="no"
                                                checked={driversLicenseAnswer === "no"}
                                                onChange={() => setDriversLicenseAnswer("no")}
                                                className="w-4 h-4 accent-black border-gray-300 focus:ring-black"
                                            />
                                            <span className="text-sm text-gray-700">No</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                            
                            {/* Visa Sponsorship Question */}
                            {visaSponsorshipOffered && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-900 mb-3">
                                        Do you need visa sponsorship? <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex flex-wrap items-center gap-4">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="visa-sponsorship-question"
                                                value="yes"
                                                checked={visaSponsorshipAnswer === "yes"}
                                                onChange={() => setVisaSponsorshipAnswer("yes")}
                                                className="w-4 h-4 accent-black border-gray-300 focus:ring-black"
                                            />
                                            <span className="text-sm text-gray-700">Yes</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="visa-sponsorship-question"
                                                value="no"
                                                checked={visaSponsorshipAnswer === "no"}
                                                onChange={() => setVisaSponsorshipAnswer("no")}
                                                className="w-4 h-4 accent-black border-gray-300 focus:ring-black"
                                            />
                                            <span className="text-sm text-gray-700">No</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {currentStep === 3 && (
                        /* Step 3: Completion Screen */
                        <div className="flex flex-col items-center justify-center py-12 px-6">
                            {/* Paper Airplane Icons */}
                            <div className="relative mb-8 w-32 h-32">
                                <svg
                                    className="absolute top-0 left-0 w-8 h-8 text-gray-300 transform rotate-12"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                                </svg>
                                <svg
                                    className="absolute top-4 right-4 w-8 h-8 text-gray-300 transform -rotate-12"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                                </svg>
                                <svg
                                    className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-8 text-gray-300 transform rotate-6"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                                </svg>
                            </div>
                            
                            {/* Application Sent Text */}
                            <h3 className="text-2xl font-semibold text-gray-900 mb-2">Application Sent</h3>
                        </div>
                    )}
                </div>

                {/* Footer with Buttons */}
                {currentStep !== 3 && (
                    <div className="px-6 py-4 border-t border-gray-200 bg-white flex items-center justify-end gap-3">
                        <button
                            onClick={currentStep === 1 ? onClose : handleBack}
                            className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
                        >
                            {currentStep === 1 ? "Cancel" : "Back"}
                        </button>
                        <button
                            onClick={currentStep === 1 ? handleStep1Next : handleStep2Next}
                            disabled={currentStep === 1 && (uploading.cv || uploading.coverLetter)}
                            className="px-6 py-3 text-sm font-medium text-white bg-black rounded-full hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                )}
                
                {/* Step 3 Footer with Done Button */}
                {currentStep === 3 && (
                    <div className="px-6 py-4 border-t border-gray-200 bg-white flex items-center justify-end gap-3">
                        <button
                            onClick={handleStep3Done}
                            className="px-6 py-3 text-sm font-medium text-white bg-gray-800 rounded-full hover:bg-gray-900 transition-colors"
                        >
                            Done
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default JobApplicationModal;

